import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Hotel, ExternalLink, ShieldCheck, MapPin, CalendarClock, BadgeDollarSign, Star, Luggage, KeyRound, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "../components/common";
import HotelProviderSearch from "../components/travel/HotelProviderSearch";
import DidYouBook from "../components/travel/DidYouBook";

const STAY_TYPES = [
  ["Hotels", "Daily housekeeping, front desk, predictable standards. Best for short city stays and business trips."],
  ["Vacation Rentals", "Kitchens, laundry and space — great for families, groups and stays of 4+ nights."],
  ["Hostels", "Budget-friendly, social, often with private rooms too. Check locker and bathroom setups."],
  ["Resorts", "All-inclusive convenience; watch for daily resort fees added at checkout."],
  ["Airport Hotels", "Ideal for early departures, long layovers and delay recovery — many offer day rates."],
  ["Aparthotels", "Hotel services with apartment living — a strong middle ground for week-long stays."],
];


const BOOKING_TIPS = [
  [CalendarClock, "When to book", "City hotels are usually cheapest 1-3 months out; resort and peak-season stays 4-6+ months ahead. Refundable rates let you rebook if prices drop."],
  [MapPin, "Location first", "A cheaper room far from transit often costs more in time and taxis. Check the walk to the nearest station and the airport connection before booking."],
  [BadgeDollarSign, "Watch the real total", "Resort fees, lodging taxes, parking and breakfast can add 15-30%. Compare final checkout totals, not nightly rates."],
  [Star, "Read recent reviews", "Sort reviews by newest — renovations, management changes and construction noise won't show in the average score."],
  [KeyRound, "Check-in logistics", "Note check-in/out times vs your flights. Most hotels store bags free before check-in — plan your first and last day around it."],
  [AlertTriangle, "Safety basics", "Confirm smoke detectors, know your floor's exits, and use the room safe for passports when you don't need to carry them."],
];

export default function Hotels() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partners?category=Hotels").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, []);
  if (!data) return <Spinner full />;

  const initial = {
    place: params.get("place") || "", checkin: params.get("checkin") || "",
    checkout: params.get("checkout") || "", adults: Number(params.get("guests")) || 2,
  };
  const tripId = params.get("trip") || null;
  const campaign = params.get("campaign") || "TP-HOTELS";

  return (
    <div data-testid="hotels-page">
      <section className="border-b border-zinc-200 bg-teal-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-teal-300 mb-3 flex items-center gap-2"><Hotel className="w-4 h-4" /> Hotels & Stays</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white max-w-3xl">Find a place to stay for your trip</h1>
          <p className="text-zinc-300 mt-4 max-w-2xl text-lg">Plan your stay with confidence, then keep your bookings organized alongside your flights in My Travel Hub.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <DidYouBook />
        <div className="mb-12">
          <HotelProviderSearch initial={initial} tripId={tripId} campaign={campaign} placement="hotels-page" />
        </div>
        {data.items.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {data.items.map((p) => (
              <a key={p.id} href={p.go_url ? `${process.env.REACT_APP_BACKEND_URL}${p.go_url}?page=hotels` : p.official_url} target="_blank" rel="noreferrer sponsored"
                data-testid={`hotel-partner-${p.slug}`} className="border border-zinc-300 bg-white p-5 hover:border-teal-500 transition-colors group">
                {p.logo_url && <img src={p.logo_url} alt={p.name} className="h-8 w-auto mb-3 object-contain" />}
                <div className="font-heading font-900 text-lg group-hover:text-teal-600 transition-colors">{p.name}</div>
                {p.description && <p className="text-sm text-muted-foreground mt-1">{p.description}</p>}
                <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-600 text-teal-600">Search stays <ExternalLink className="w-3.5 h-3.5" /></span>
              </a>
            ))}
          </div>
        )}

        {/* Stay-planning guide */}
        <section data-testid="hotels-guide">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight mb-2">How to plan a better stay</h2>
          <p className="text-zinc-600 mb-6 max-w-2xl">Six things seasoned travelers check before booking — wherever you book.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {BOOKING_TIPS.map(([Icon, title, body]) => (
              <div key={title} className="border border-zinc-300 bg-white p-5">
                <Icon className="w-5 h-5 text-teal-600 mb-2" />
                <div className="font-heading font-700 text-base">{title}</div>
                <p className="text-sm text-zinc-600 mt-1">{body}</p>
              </div>
            ))}
          </div>

          <h2 className="font-heading font-900 text-2xl tracking-tight mb-4">Which type of stay fits your trip?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12" data-testid="hotels-stay-types">
            {STAY_TYPES.map(([title, body]) => (
              <div key={title} className="border border-zinc-200 bg-zinc-50 p-4">
                <div className="font-700 text-sm">{title}</div>
                <p className="text-xs text-zinc-600 mt-1">{body}</p>
              </div>
            ))}
          </div>

          <div className="border border-teal-600 bg-teal-50/50 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="hotels-hub-cta">
            <Luggage className="w-8 h-8 text-teal-700 shrink-0" />
            <div className="flex-1">
              <div className="font-heading font-700 text-lg">Keep every reservation in one place</div>
              <p className="text-sm text-zinc-600">Save hotel confirmations, addresses and check-in notes to your trip in My Travel Hub — next to your flights, packing list and expenses.</p>
            </div>
            <Link to="/travel-hub" data-testid="hotels-hub-link" className="px-5 py-2.5 bg-teal-700 text-white text-sm font-700 hover:bg-teal-800 transition-colors shrink-0">Open My Travel Hub</Link>
          </div>
          {data.items.length === 0 && (
            <p className="text-xs text-muted-foreground mt-6">Hotel booking partners are on the way — this page will let you compare stays directly once they arrive.</p>
          )}
        </section>

        {data.disclosure && (
          <p className="text-xs text-muted-foreground mt-8 border-t border-zinc-200 pt-4 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" /> {data.disclosure}
          </p>
        )}
      </div>
    </div>
  );
}
